import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import { Fieldset, LargeOnly, NoPrintDiv } from "commons";
import { STATE } from "commons/src/PDFWrapper";
import {
    isValidIncomeHistoryEvent,
    isValidIncomeHistoryInvestment
} from "commons/src/helpers/IncomeHistoryHelpers";

import { SelectDateRangeWithType } from "./SelectDateRangeWithType";
import { TransactionViewOptions } from "./TransactionViewOptions";
import { LoadingStateDisplay } from "./LoadingStateDisplay";
import { TransactionItem } from "./TransactionItem";
import { InvestmentTransaction } from "./InvestmentTransaction";
import { TransactionTableDisplay } from "./TransactionTableDisplay";
import { TotalTR } from "./TotalTR";
import {
    getFeeLowLevel,
    investmentAllowedByFilters,
    investmentOrderingComparator,
    shouldRenderThisInvestmentTransaction,
    shouldRenderThisTransactionEvent,
    showTotal
} from "./ShowHideUtils";
import { LOADING_STATE } from "./DataLoadingStates";
import { ShowInClientPortal } from "./ShowInClientPortal";
import { SearchInput } from "./SearchInput";
import { TransactionDownloads } from "./TransactionDownloads";

export class TransactionViewerTable extends Component {
    state = {
        selectedDateRange: ""
    };

    componentDidUpdate() {
        this.props.updateIframe();
    }

    buildInvestmentRowsForEvent = (item, investTransRows, assets) => {
        let hasChildren = false;
        let fees = 0;
        let hasNoValidInvestments = false;
        const investments = [].concat
            .apply(
                [],
                item.accountTransactions.map(actr => actr.investmentTransactions)
            )
            .filter(investmentTransaction =>
                investmentAllowedByFilters(
                    this.props.filterOptions,
                    investmentTransaction,
                    this.props.isIncomeHistory
                )
            )
            .filter(investmentTransaction =>
                this.props.isIncomeHistory
                    ? this.filterIncomeHistoryInvestments(investmentTransaction)
                    : true
            )
            .sort(investmentOrderingComparator);

        if (!investments.length) hasNoValidInvestments = true;
        investments.forEach(investmentTransaction => {
            const investmentNarrative = InvestmentTransaction.makeNarrative(investmentTransaction);
            if (
                shouldRenderThisInvestmentTransaction(
                    this.props.searchValue,
                    this.props.showInvestment[item.trevId],
                    investmentNarrative
                )
            ) {
                fees +=
                    investmentTransaction.itrnFees === null
                        ? 0
                        : investmentTransaction.itrnFees.reduce(
                              (a, b) => (b.xptpShortDescription !== undefined ? a : a + b),
                              0
                          );
                investTransRows.push(
                    <InvestmentTransaction
                        key={`itrn-${investmentTransaction.itrnId}`}
                        investmentTransaction={investmentTransaction}
                        narrative={investmentNarrative}
                        isClientPortal={this.props.isClientPortal}
                        isIncomeHistory={this.props.isIncomeHistory}
                    />
                );
            }
            hasChildren = true;
            if (investmentTransaction.fund.isin) {
                assets[investmentTransaction.fund.isin] = investmentTransaction.fund.longName;
            }
        });
        return { hasChildren, fees, hasNoValidInvestments };
    };

    buildTransactionItem = (item, hasChildren, desc) => (
        <TransactionItem
            key={`trev-${item.trevId}`}
            isShowInvestments={this.props.showInvestment[item.trevId]}
            hideShowInvestments={this.props.hideShowInvestments}
            hasChildren={hasChildren}
            runningCash={this.props.runningCash}
            accountProductType={this.props.accountProductType}
            transactionEvent={item}
            filteredFees={item.trevFees.filter(
                fee =>
                    this.props.filterOptions["Fees and charges"][
                        getFeeLowLevel(fee.xptpShortDescription)
                    ].selected
            )}
            searchApplied={this.props.searchValue.length > 0}
            narrative={desc}
            isClientPortal={this.props.isClientPortal}
            isIncomeHistory={this.props.isIncomeHistory}
        />
    );

    getTotals = (newInvestRowsInfo, transEvent) => {
        const expanded =
            (this.props.showInvestment[transEvent.trevId] || this.props.searchValue.length > 0) &&
            newInvestRowsInfo.hasChildren;
        let fees = 0;
        let amount = 0;

        // investment trans fees (post-expansion, excl. trade fees)
        if (newInvestRowsInfo.fees !== 0 && expanded) {
            fees += newInvestRowsInfo.fees;
        }

        // investment trans fees (pre-expansion)
        if (transEvent.trevFees.length > 0 && !expanded) {
            fees +=
                transEvent.trevFees
                    .filter(
                        fee =>
                            this.props.filterOptions["Fees and charges"][
                                getFeeLowLevel(fee.xptpShortDescription)
                            ].selected
                    )
                    .reduce((a, b) => {
                        return a + b.dtrnDeductionAmount;
                    }, 0) * -1;
        }

        // fees for events that are actually fees (e.g Advice fees at event level)
        if (
            transEvent.accountTransactions[0] &&
            transEvent.accountTransactions[0].investmentTransactions[0] &&
            transEvent.accountTransactions[0].investmentTransactions[0].itrnMappings
                .TRX_HIGH_LEVEL === "Fees and charges"
        ) {
            fees += transEvent.trevAmount;
        } else {
            amount += transEvent.trevAmount;
        }

        return { fees, amount };
    };

    filterIncomeHistoryTransactionEvents = transactionEventResourceList =>
        transactionEventResourceList.filter(trev => isValidIncomeHistoryEvent(trev));

    filterIncomeHistoryInvestments = investment =>
        investment.fake === true || isValidIncomeHistoryInvestment(investment);

    handleDateChange = (event, dateSelection) => {
        this.setState({ selectedDateRange: dateSelection.label });
        this.props.dateChanged(event, dateSelection);
    };

    render() {
        const assets = {};
        const transactionTypes = {};
        let rows = [];
        let total = 0;
        let fees = 0;
        let noIHData = false;

        if ("_embedded" in this.props.results) {
            let transactionEventResourceList =
                this.props.results._embedded.transactionEventResourceList;
            if (this.props.isIncomeHistory) {
                transactionEventResourceList = this.filterIncomeHistoryTransactionEvents(
                    transactionEventResourceList
                );
            }
            transactionEventResourceList.forEach(item => {
                const investTransRows = [];
                const newInvestRowsInfo = this.buildInvestmentRowsForEvent(
                    item,
                    investTransRows,
                    assets
                );
                const trevDesc = TransactionItem.getTrevDescription(item);
                if (
                    shouldRenderThisTransactionEvent(
                        this.props.searchValue,
                        trevDesc,
                        investTransRows.length,
                        item,
                        this.props.filterOptions,
                        this.props.isIncomeHistory,
                        newInvestRowsInfo.hasNoValidInvestments
                    )
                ) {
                    rows.push(
                        this.buildTransactionItem(item, newInvestRowsInfo.hasChildren, trevDesc)
                    );
                    transactionTypes[item.trevTtypDescription] = 0;
                    if (investTransRows.length) {
                        rows = rows.concat(investTransRows);
                    }
                    const individualTotals = this.getTotals(newInvestRowsInfo, item);
                    fees += individualTotals.fees;
                    total += individualTotals.amount;
                }
            });

            const searchBarOverridesTotal =
                this.props.searchValue.length > 0 && this.props.searchTotalDisplay;
            if (showTotal(this.props.filterOptions, searchBarOverridesTotal)) {
                rows.push(
                    <TotalTR
                        key="total"
                        feesAmount={fees}
                        amount={total}
                        isClientPortal={this.props.isClientPortal}
                        isIncomeHistory={this.props.isIncomeHistory}
                    />
                );
            }
            if (!rows.length) noIHData = true;
        }

        const isLoading = this.props.totalPages !== LOADING_STATE.LOADED && !this.props.isError;
        const legendText = this.props.isClientPortal ? "" : "Transaction history";
        const isAccountSelected = this.props.accountProductType;

        return (
            <NoPrintFieldset isClientPortal={this.props.isClientPortal}>
                <NoPrintLegend>{legendText}</NoPrintLegend>
                <ShowInClientPortal isClientPortal={this.props.isClientPortal}>
                    {!this.props.isClientPortal && (
                        <FlexContainer>
                            <SelectDateRangeWithType
                                dateChanged={this.handleDateChange}
                                startDate={this.props.startDate}
                                endDate={this.props.endDate}
                                disabled={isLoading}
                            />
                            <SearchInput
                                onChange={this.props.handleSearchInput}
                                assets={assets}
                                transactionTypes={transactionTypes}
                                isClientPortal={false}
                            />
                        </FlexContainer>
                    )}
                    <MenuGroup float="left">
                        <TransactionViewOptions
                            buttonsDisabled={isLoading}
                            filterOptions={this.props.filterOptions}
                            filterOrdinals={this.props.filterOrdinals}
                            toggleInvestments={this.props.toggleInvestments}
                            isCollapsed={this.props.isCollapsed}
                            isClientPortal={this.props.isClientPortal}
                            filterSelect={this.props.filterSelect}
                            expandHidden={this.props.searchValue.length > 0}
                            assets={assets}
                            transactionTypes={transactionTypes}
                        />
                        {isAccountSelected && (
                            <TransactionDownloads
                                onPrint={this.props.triggerDownload}
                                onPrintCSV={this.props.onPrintCSV}
                                isClientPortal={this.props.isClientPortal}
                                loading={isLoading}
                                isPrinting={this.props.printState === STATE.PRINTING}
                            />
                        )}
                        {this.props.isClientPortal && (
                            <LargeOnly enabled={this.props.isClientPortal}>
                                <SearchInput
                                    onChange={this.props.handleSearchInput}
                                    assets={assets}
                                    transactionTypes={transactionTypes}
                                    isClientPortal={this.props.isClientPortal}
                                />
                            </LargeOnly>
                        )}
                        <LoadingStateDisplay
                            isError={this.props.isError}
                            isClientPortal={this.props.isClientPortal}
                            isIncomeHistory={this.props.isIncomeHistory}
                            noData={this.props.noData}
                            noIHData={noIHData}
                            loading={isLoading}
                            printFailed={this.props.printState === STATE.ERROR}
                            selectedDateRange={this.state.selectedDateRange}
                        />
                    </MenuGroup>
                </ShowInClientPortal>
                <TransactionTableDisplay
                    rows={rows}
                    isClientPortal={this.props.isClientPortal}
                    isIncomeHistory={this.props.isIncomeHistory}
                />
            </NoPrintFieldset>
        );
    }
}

const NoPrintLegend = NoPrintDiv.withComponent("legend");

const NoPrintFieldset = styled(Fieldset)`
    ${props => props.isClientPortal && "visibility: hidden;"}
    ${props => props.isClientPortal && "padding: 0;"}

    @media print {
        visibility: hidden;
    }
`;

const MenuGroup = styled(NoPrintDiv)`
    clear: left;
    width: 100%;
    margin-top: 5px;
`;

const FlexContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
`;

TransactionViewerTable.propTypes = {
    showInvestment: PropTypes.object,
    results: PropTypes.object,
    startDate: PropTypes.object,
    endDate: PropTypes.object,
    dateChanged: PropTypes.func,
    runningCash: PropTypes.object,
    filterOptions: PropTypes.object,
    filterOrdinals: PropTypes.array,
    handleSearchInput: PropTypes.func,
    searchValue: PropTypes.string,
    searchTotalDisplay: PropTypes.bool,
    totalPages: PropTypes.number,
    isCollapsed: PropTypes.bool,
    isError: PropTypes.bool,
    noData: PropTypes.bool,
    hideShowInvestments: PropTypes.func,
    toggleInvestments: PropTypes.func,
    filterSelect: PropTypes.func,
    updateIframe: PropTypes.func,
    accountProductType: PropTypes.string,
    onPrintCSV: PropTypes.func,
    onPrint: PropTypes.func,
    printState: PropTypes.number,
    isClientPortal: PropTypes.bool,
    isIncomeHistory: PropTypes.bool,
    triggerDownload: PropTypes.func
};

TransactionViewerTable.defaultProps = {
    isIncomeHistory: false
};
